// SideTabs.js

import React, { useEffect, useRef, useMemo } from 'react';
import { Tab } from 'semantic-ui-react';
import Lowlight from 'react-lowlight';
import javascript from 'highlight.js/lib/languages/javascript';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Register the JavaScript language for syntax highlighting
Lowlight.registerLanguage('js', javascript);

function TextComponent(props) {
    const editorRef = useRef(null);
    
    useEffect(() => {
        if (props.description && editorRef.current) {
            const editor = editorRef.current.getEditor();
            if (editor) {
                const container = editor.container;
                const scrollElement = container.querySelector('.ql-editor');
                if (scrollElement) {
                    scrollElement.scrollTop = scrollElement.scrollHeight;
                }
            }
        }
    }, [props.description]);
    
    if (!props.description) {
        return <div></div>;
    }

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'indent'
    ];

    const modules = useMemo(() => ({
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            ['clean']
        ],
        clipboard: {
            matchVisual: false
        },
        keyboard: {
            bindings: {}
        }
    }), []);

    return (
        <ReactQuill
            ref={editorRef}
            value={props.description}
            readOnly={true}
            theme="bubble"
            modules={modules}
            formats={formats}
        />
    );
}

function EditorComponent(props) {
    const quillRef = useRef(null);
    const saveButtonRef = useRef(null);
    const styleRef = useRef(null);

    useEffect(() => {
        if (saveButtonRef.current) {
            const button = saveButtonRef.current;
            if (props.show_save_buttons) {
                button.style.display = 'inline-block';
            } else {
                button.style.display = 'none';
            }
        }
    }, [props.show_save_buttons]);

    useEffect(() => {
        if (typeof window !== 'undefined' && !saveButtonRef.current) {
            const toolbar = document.querySelector('.ql-toolbar');
            if (!toolbar) return;

            const Quill = require('react-quill-new').Quill;
            
            // Add a save button to toolbar
            const saveButton = document.createElement('span');
            saveButtonRef.current = saveButton;
            saveButton.className = 'ql-formats';
            saveButton.style.display = props.show_save_buttons ? 'inline-block' : 'none';
            saveButton.innerHTML = `
                <button class="ql-save" type="button" data-tooltip="Save changes">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width: 18px; height: 18px; transform: scale(1.35);">
                        <path d="M17 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-4-4z"></path>
                        <polyline points="17 3 17 7 7 7 7 3"></polyline>
                        <line x1="7" y1="13" x2="17" y2="13"></line>
                    </svg>
                </button>
            `;

            const button = saveButton.querySelector('button');
            if (button) {
                button.style.cssText = `
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    padding: 3px 5px;
                    height: 28px;
                    width: 32px;
                    color: #444;
                    background: none;
                    border: none;
                    cursor: pointer;
                    border-radius: 3px;
                    position: relative;
                `;
                button.addEventListener('mouseover', () => {
                    button.style.background = '#e9e9e9';
                });
                button.addEventListener('mouseout', () => {
                    button.style.background = 'none';
                });
                button.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (props.onSave) {
                        props.onSave();
                    }
                });
            }

            toolbar.appendChild(saveButton);

            // Add custom CSS to ensure proper spacing and tooltip
            const style = document.createElement('style');
            styleRef.current = style;
            style.textContent = `
                .ql-toolbar.ql-snow {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 5px;
                    padding: 8px;
                }
                .ql-toolbar .ql-formats {
                    margin-right: 5px !important;
                }
                .ql-toolbar .ql-formats:last-child {
                    margin-right: 0 !important;
                    margin-left: auto !important;
                }
                .ql-save:hover {
                    color: #06c !important;
                }
                .ql-save svg {
                    position: relative;
                    top: -1px;
                }
                [data-tooltip] {
                    position: relative;
                }
                [data-tooltip]:hover:after {
                    content: attr(data-tooltip);
                    position: absolute;
                    bottom: -30px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: #2c2c2c;
                    color: white;
                    padding: 5px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    white-space: nowrap;
                    z-index: 1000;
                }
                [data-tooltip]:hover:before {
                    content: '';
                    position: absolute;
                    bottom: -10px;
                    left: 50%;
                    transform: translateX(-50%);
                    border-width: 5px;
                    border-style: solid;
                    border-color: transparent transparent #2c2c2c transparent;
                    z-index: 1000;
                }
            `;
            document.head.appendChild(style);
        }

        // Cleanup function
        return () => {
            if (saveButtonRef.current) {
                saveButtonRef.current.remove();
                saveButtonRef.current = null;
            }
            if (styleRef.current) {
                styleRef.current.remove();
                styleRef.current = null;
            }
        };
    }, []); // Empty dependency array since we only want this to run once on mount

    const modules = useMemo(() => ({
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['clean']
        ]
    }), []);

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'indent'
    ];

    const handleChange = (content, delta, source, editor) => {
        props.setDescription(editor.getHTML());
    };

    return (
        <ReactQuill
            ref={quillRef}
            theme="snow"
            value={props.description || ''}
            modules={modules}
            formats={formats}
            onChange={handleChange}
            readOnly={false}
        />
    );
}

function SideTabs(props) {
    useEffect(() => {
        if (props.isStudentAnswering) {
            props.setVisible(true);
        }
    }, [props.isStudentAnswering, props.setVisible]);

    let panes = [];
    
    if (props.isStudentAnswering) {
      
        panes.push({
            menuItem: l.question,
            render: () => (
                <Tab.Pane attached={false}>
                    <TextComponent description={props.question_description} />
                </Tab.Pane>
            ),
        });

        panes.push({
            menuItem: l.submit_answer,
            render: () => (
                <Tab.Pane attached={false}>
                    <EditorComponent
                        editable={true}
                        description={props.description}
                        setDescription={props.setDescription}
                        onSave={props.onSave}
                        show_save_buttons={props.show_save_buttons}
                    />
                </Tab.Pane>
            ),
        });
    } else if (props.isEditingAssignment) {
        panes.push({
            menuItem: l.question,
            render: () => (
                <Tab.Pane attached={false}>
                    <EditorComponent
                        editable={true}
                        description={props.description}
                        setDescription={props.setDescription}
                        onSave={props.onSave}
                        show_save_buttons={props.show_save_buttons}
                    />
                </Tab.Pane>
            ),
        });
    } else {
        // This is a note!
        if (props.isCreator) {
            panes.push({
                menuItem: l.notes,
                render: () => (
                    <Tab.Pane attached={false}>
                        <EditorComponent
                            editable={true}
                            description={props.description}
                            setDescription={props.setDescription}
                            onSave={props.onSave}
                            show_save_buttons={props.show_save_buttons}
                        />
                    </Tab.Pane>
                ),
            });
        } else {
            panes.push({
                menuItem: l.notes,
                render: () => (
                    <Tab.Pane attached={false}>
                        {props.labPageNotSigned || props.isCreator ? (
                            <EditorComponent
                                editable={true}
                                description={props.description}
                                setDescription={props.setDescription}
                                onSave={props.onSave}
                                show_save_buttons={props.show_save_buttons}
                            />
                        ) : (
                            <TextComponent description={props.description} />
                        )}
                    </Tab.Pane>
                ),
            });
        }

        panes.push({
            menuItem: 'NGSPice',
            render: () => (
                <Tab.Pane style={{ backgroundColor: '#F0F0F0' }} attached={false}>
                    <Lowlight language="js" value={props.NGSpiceNetList} />
                </Tab.Pane>
            ),
        });
    }

    return props.useNGSpice || props.isEditingAssignment || props.isStudentAnswering ? (
        <Tab
            style={{ width: '100%', height: '100%' }}
            defaultActiveIndex={props.ngSpiceSelected ? 1 : 0}
            menu={{ secondary: true, pointing: true }}
            panes={panes}
        />
    ) : props.isCreator || props.labPageNotSigned ? (
        <EditorComponent
            editable={true}
            description={props.description}
            setDescription={props.setDescription}
            onSave={props.onSave}
            show_save_buttons={props.show_save_buttons}
        />
    ) : (
        <TextComponent description={props.description} />
    );
}

export default SideTabs;
